import {
  CompanyPartnersMappedResponse,
  CompanyPartnersResponse,
  companyPartnersResponseSchema,
  CreateWorkflowResponse,
  createWorkflowResponseSchema,
  triggerDataResponseSchema,
  WorkflowDetailsResponse,
  workflowDetailsResponseSchema,
  WorkflowListingResponse,
  workflowListingResponseSchema,
} from 'api/services/workflow/workflow.schema';
import { MappedWorkflowNode } from 'ogp/components/views/management/workflows/common/workflow-editor/mappers/map-canvas-content.types';
import { Site } from 'shared/hooks';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { GetWorkflowListingParams, WorkflowDetailsResponseDto } from './workflow-service.types';
import { TriggerDataResponse } from './workflow.schema';

export class WorkflowService {
  constructor(private readonly ogpApiService: OgpApiService) {}

  public async getWorkflowsListing(
    site: Site,
    params: GetWorkflowListingParams
  ): Promise<WorkflowListingResponse> {
    const response = await this.getWorkflowsListingImplementation(site, params);
    return workflowListingResponseSchema.parse(response);
  }

  public getTriggerData = async (site: Site): Promise<TriggerDataResponse> => {
    const response = await this.getTriggerDataImplementation(site);
    return triggerDataResponseSchema.parse(response);
  };

  public async createWorkflow(
    site: Site,
    name: string,
    graphData: MappedWorkflowNode[]
  ): Promise<CreateWorkflowResponse> {
    const response = await this.createWorkflowImplementation(site, name, graphData);
    return createWorkflowResponseSchema.parse(response);
  }

  public async getWorkflowDetails(
    site: Site,
    workflowId: string
  ): Promise<WorkflowDetailsResponse> {
    const response = await this.getWorkflowDetailsImplementation(site, workflowId);
    return workflowDetailsResponseSchema.parse(response);
  }

  public async archiveWorkflow(site: Site, workflowId: string): Promise<void> {
    switch (site) {
      case 'ogp':
        await this.ogpApiService.create({
          path: `workflow-management/${workflowId}/archive`,
        });
        break;
      default:
        throw new Error('Not implemented');
    }
  }

  public async getCompanyPartners(site: Site): Promise<CompanyPartnersMappedResponse> {
    const response = await this.getCompanyPartnersImplementation(site);

    const parsed = companyPartnersResponseSchema.parse(response);

    return parsed.map((p) => ({
      label: p.partner.name,
      value: p.partner.id,
    }));
  }

  private async getWorkflowDetailsImplementation(
    site: Site,
    workflowId: string
  ): Promise<WorkflowDetailsResponseDto> {
    switch (site) {
      case 'ogp':
        return await this.ogpApiService.get<WorkflowDetailsResponseDto>({
          path: `workflow-management/${workflowId}`,
        });
    }
    throw new Error('Not implemented');
  }
  private async getWorkflowsListingImplementation(
    site: Site,
    params: GetWorkflowListingParams
  ): Promise<unknown> {
    switch (site) {
      case 'ogp':
        return await this.ogpApiService.get<WorkflowListingResponse>({
          path: 'workflow-management/workflow-list',
          params: { page: params.page, size: params.size },
        });
      default:
        throw new Error('Not implemented');
    }
  }

  private async getTriggerDataImplementation(site: Site): Promise<TriggerDataResponse> {
    switch (site) {
      case 'ogp':
        return await this.ogpApiService.get<TriggerDataResponse>({
          path: 'workflow-management/trigger-data',
        });
      default:
        throw new Error('Not implemented');
    }
  }
  private async createWorkflowImplementation(
    site: Site,
    name: string,
    graph: MappedWorkflowNode[]
  ): Promise<unknown> {
    switch (site) {
      case 'ogp':
        return await this.ogpApiService.create<CreateWorkflowResponse>({
          path: 'workflow-management/create',
          body: {
            name,
            graph,
          },
        });
      default:
        throw new Error('Not implemented');
    }
  }

  private async getCompanyPartnersImplementation(site: Site): Promise<CompanyPartnersResponse> {
    switch (site) {
      case 'ogp':
        return await this.ogpApiService.get<CompanyPartnersResponse>({
          path: 'workflow-management/company-partners',
        });
      default:
        throw new Error('Not implemented');
    }
  }
}
